/**
 * I'm Not a Robot — escalating CAPTCHAs.
 *
 * Two challenge shapes only, so the whole game stays one small state machine:
 *  - `grid`: pick every tile whose index is in `answer`
 *  - `text`: type something matching `accepts`
 *
 * The joke is that the *questions* get less answerable while the interface stays
 * completely deadpan. Every one still has a defensible answer — a CAPTCHA you
 * cannot pass is a dead end, not a joke.
 */
export type Challenge =
  | {
      kind: 'grid';
      prompt: string;
      /** 9 tiles. */
      tiles: string[];
      /** Indices that must be selected, exactly. */
      answer: number[];
      /** Shown after passing. */
      note: string;
    }
  | {
      kind: 'text';
      prompt: string;
      /** Rendered as the distorted thing to read, if any. */
      display?: string;
      accepts: (value: string) => boolean;
      hint: string;
      note: string;
    };

const clean = (value: string) => value.trim().toLowerCase();

export const CHALLENGES: Challenge[] = [
  {
    kind: 'grid',
    prompt: 'Select all squares with a traffic light.',
    tiles: ['🚦', '🌳', '🚗', '🏠', '🚦', '🐕', '🌲', '🚲', '🚦'],
    answer: [0, 4, 8],
    note: 'Standard. Enjoy it while it lasts.'
  },
  {
    kind: 'grid',
    prompt: 'Select all squares containing a vehicle.',
    tiles: ['🚌', '🍎', '🚁', '🪑', '🛵', '📚', '🚂', '🕯️', '🧦'],
    answer: [0, 2, 4, 6],
    note: 'You are doing well. That will change.'
  },
  {
    kind: 'text',
    prompt: 'Type the characters you see.',
    display: 'fᴀᴄᴛiii',
    accepts: (v) => clean(v).replace(/[^a-z0-9]/g, '') === 'factiii',
    hint: 'It says factiii.',
    note: 'A machine could have done that. Continuing anyway.'
  },
  {
    kind: 'grid',
    prompt: 'Select all squares that contain a feeling.',
    tiles: ['🪑', '😌', '🧱', '😰', '🔩', '🥲', '📎', '🧊', '🔧'],
    answer: [1, 3, 5],
    note: 'Three of those were furniture and hardware. You knew.'
  },
  {
    kind: 'grid',
    prompt: 'Select all squares containing something that has never existed.',
    tiles: ['🦕', '🦄', '🐋', '🧜', '🐈', '🐉', '🦣', '🦭', '🧞'],
    answer: [1, 3, 5, 8],
    note: 'The mammoth and the dinosaur did exist. Briefly.'
  },
  {
    kind: 'text',
    prompt: 'How many of these words are in this sentence?',
    display: 'How many of these words are in this sentence?',
    accepts: (v) => clean(v).replace(/[^0-9]/g, '') === '8',
    hint: 'Count the words in the sentence shown.',
    note: 'Eight. We checked, repeatedly, and argued about it.'
  },
  {
    kind: 'grid',
    prompt: 'Select all squares you would describe as load-bearing.',
    tiles: ['🧱', '🎈', '🏛️', '🫧', '🌉', '🪶', '🗜️', '☁️', '🍃'],
    answer: [0, 2, 4, 6],
    note: 'Brick, column, bridge, clamp. The balloon is not structural.'
  },
  {
    kind: 'text',
    prompt: 'Prove you are human. What did you have for breakfast?',
    accepts: (v) => clean(v).length > 0,
    hint: 'Anything at all. We cannot verify this and we both know it.',
    note: 'Unverifiable, accepted, and filed nowhere.'
  },
  {
    kind: 'grid',
    prompt: 'Select the squares that are watching you.',
    tiles: ['👁️', '🪟', '👀', '🚪', '📷', '🧿', '🪑', '🖼️', '🔭'],
    answer: [0, 2, 4, 5, 8],
    note: 'Eye, eyes, camera, the nazar, telescope. The chair is not watching.'
  },
  {
    kind: 'text',
    prompt: 'Final check. Are you a robot?',
    accepts: (v) => ['no', 'nope', 'not a robot', 'n'].includes(clean(v)),
    hint: 'Type “no”. A robot would also type no, which is the flaw.',
    note: 'Verified. This proved nothing. You are free to go.'
  }
];
